import {
    createBrowserRouter,
    Navigate,
    RouterProvider,
    useNavigate,
    useRouteError,
    useSearchParams
} from "react-router-dom";
import {useEffect, useRef, useState} from "react";
import {useAuth} from "../hooks/useAuth.ts";
import {authServiceRtk} from "../services/authService.ts";
import {DialogType, useDialogContext} from "../contexts/DialogContext";
import App from "../App";
import {DashboardView} from "../components/views/user/DashboardView";
import {LicensesView} from "../components/views/user/LicensesView";
import {TeamMembersView} from "../components/views/user/TeamMembersView";
import {SupportView} from "../components/views/user/SupportView";
import {SettingsView} from "../components/views/user/SettingsView";
import {PricingView} from "../components/views/user/PricingView";
import {DownloadView} from "../components/views/user/DownloadView";
import {JoinView} from "../components/views/user/JoinView";
import {AdminUsersView} from "../components/views/admin/AdminUsersView/index";
import {AdminLicensesView} from "../components/views/admin/AdminLicensesView/index";
import {AdminTeamsView} from "../components/views/admin/AdminTeamsView/index";
import {AdminProductsView} from "../components/views/admin/AdminProductsView/index";
import {TosAcceptanceDialog} from "../components/dialogs/user/TosAcceptanceDialog";
import {WelcomeDialog} from "../components/dialogs/user/WelcomeDialog";
import ErrorFallback from "../components/shared/errors/ErrorFallback";
import {RootElement} from "./root";

const ProtectedRoute = ({children}: { children: React.ReactNode }) => {
    const {isAuthenticated, isLoading, login} = useAuth();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        const urlParams = new URLSearchParams(window.location.search);
        const hasTokenInUrl = urlParams.has('token') || urlParams.has('error');

        if (!hasTokenInUrl && !authServiceRtk.isAuthenticated()) {
            // Store intended path with query parameters
            const intendedPath = window.location.pathname + window.location.search;
            sessionStorage.setItem('intendedPath', intendedPath);
            const screenHint = window.location.pathname === '/join' ? 'sign-up' : undefined;
            login(window.location.pathname + window.location.search, screenHint);
            return (
                <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Redirecting to login...</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Processing authentication...</p>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};

const PlatformAdminRoute = ({children}: { children: React.ReactNode }) => {
    const {user, isAuthenticated, isLoading} = useAuth();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-muted-foreground">Authentication required</p>
                </div>
            </div>
        );
    }

    if (!user?.roles?.includes('PLATFORM_ADMIN')) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-muted-foreground">Access denied. Platform administrator privileges required.</p>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};

const AdminRoute = ({children}: { children: React.ReactNode }) => {
    const {user, isAuthenticated, isLoading} = useAuth();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-muted-foreground">Authentication required</p>
                </div>
            </div>
        );
    }

    const hasManagerRole = user?.roles?.some(role => ['ADMIN', 'PLATFORM_ADMIN'].includes(role));
    if (!hasManagerRole) {
        return <Navigate to="/settings" replace/>;
    }

    return <>{children}</>;
};

const RouteError = () => {
    const error = useRouteError();
    const navigate = useNavigate();

    const errorObj = error instanceof Error ? error : new Error(String(error));

    return <ErrorFallback error={errorObj} onReset={() => navigate('/dashboard')}/>;
};

const DefaultRedirect = () => {
    const {user, isLoading} = useAuth();
    const [searchParams] = useSearchParams();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    // Check if there's a stored intended path from external link
    const storedPath = sessionStorage.getItem('intendedPath');

    if (storedPath) {
        sessionStorage.removeItem('intendedPath');
        // If the stored path is root, redirect to dashboard instead
        const redirectPath = storedPath === '/' ? '/dashboard' : storedPath;
        return <Navigate to={redirectPath} replace/>;
    }

    // Default redirect to dashboard for all authenticated users
    // Preserve query parameters from current URL
    const queryString = searchParams.toString();
    const dashboardPath = queryString ? `/dashboard?${queryString}` : '/dashboard';
    return <Navigate to={dashboardPath} replace/>;
};

const router = createBrowserRouter([
    {
        path: "/",
        element: <RootElement/>,
        children: [
            {
                path: "/",
                element: <DefaultRedirect/>,
            },
            {
                path: "/*",
                element: (
                    <ProtectedRoute>
                        <App/>
                    </ProtectedRoute>
                ),
                errorElement: <RouteError/>,
                children: [
            {
                path: "dashboard",
                element: (
                    <AdminRoute>
                        <DashboardView/>
                    </AdminRoute>
                ),
            },
            {
                path: "licenses",
                element: (
                    <AdminRoute>
                        <LicensesView/>
                    </AdminRoute>
                ),
            },
            {
                path: "team",
                element: (
                    <AdminRoute>
                        <TeamMembersView/>
                    </AdminRoute>
                ),
            },
            {
                path: "download",
                element: (
                    <ProtectedRoute>
                        <DownloadView/>
                    </ProtectedRoute>
                ),
            },
            {
                path: "pricing",
                element: (
                    <AdminRoute>
                        <PricingView/>
                    </AdminRoute>
                ),
            },
            {
                path: "support",
                element: (
                    <ProtectedRoute>
                        <SupportView/>
                    </ProtectedRoute>
                ),
            },
            {
                path: "settings",
                element: (
                    <ProtectedRoute>
                        <SettingsView/>
                    </ProtectedRoute>
                ),
            },
            {
                path: "join",
                element: (
                    <AdminRoute>
                        <JoinView/>
                    </AdminRoute>
                ),
            },
            {
                path: "admin/users",
                element: (
                    <PlatformAdminRoute>
                        <AdminUsersView/>
                    </PlatformAdminRoute>
                ),
            },
            {
                path: "admin/licenses",
                element: (
                    <PlatformAdminRoute>
                        <AdminLicensesView/>
                    </PlatformAdminRoute>
                ),
            },
            {
                path: "admin/teams",
                element: (
                    <PlatformAdminRoute>
                        <AdminTeamsView/>
                    </PlatformAdminRoute>
                ),
            },
            {
                path: "admin/products",
                element: (
                    <PlatformAdminRoute>
                        <AdminProductsView/>
                    </PlatformAdminRoute>
                ),
            },
                ],
            },
        ],
    },
]);

export function AppRouter() {
    const {user, isAuthenticated, isLoading, refreshUser} = useAuth();
    const {openDialog, activeDialog, closeDialog} = useDialogContext();
    const [hasOpenedTosDialog, setHasOpenedTosDialog] = useState(false);
    const hasOpenedWelcomeDialogRef = useRef(false);

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            const hasAcceptedTos = user?.flags?.includes('HAS_ACCEPTED_TOS');
            if (!hasAcceptedTos && !hasOpenedTosDialog) {
                openDialog(DialogType.TOS);
                setHasOpenedTosDialog(true);
            }
        }
    }, [user, isAuthenticated, isLoading, hasOpenedTosDialog]);

    useEffect(() => {
        if (!isLoading && isAuthenticated && !hasOpenedWelcomeDialogRef.current) {
            const hasAcceptedTos = user?.flags?.includes('HAS_ACCEPTED_TOS');
            const hasSeenWelcome = localStorage.getItem("hasSeenWelcome");

            if (hasAcceptedTos && !hasSeenWelcome) {
                hasOpenedWelcomeDialogRef.current = true;
                openDialog(DialogType.WELCOME);
            }
        }
    }, [user, isAuthenticated, isLoading]);

    const handleTosAccepted = async () => {
        await refreshUser();
        setHasOpenedTosDialog(false);
        closeDialog(DialogType.TOS);
    };

    const handleWelcomeDialogClose = (open: boolean) => {
        if (!open) {
            localStorage.setItem("hasSeenWelcome", "true");
            closeDialog(DialogType.WELCOME);
        }
    };

    const handleDownload = () => {
        window.location.href = "/download";
    };

    const handleInviteTeam = () => {
        window.location.href = "/team";
    };

    return (
        <>
            <RouterProvider router={router}/>
            <TosAcceptanceDialog open={activeDialog === DialogType.TOS} onSuccess={handleTosAccepted}/>
            <WelcomeDialog
                open={activeDialog === DialogType.WELCOME}
                onOpenChange={handleWelcomeDialogClose}
                onDownload={handleDownload}
                onInviteTeam={handleInviteTeam}
            />
        </>
    );
}